import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "./client";
import type { components } from "./schema";

export type Me = components["schemas"]["Me"];
export type Connection = components["schemas"]["ConnectionDto"];
export type SyncRun = components["schemas"]["SyncRunDto"];
export type Signal = components["schemas"]["SignalDto"];
export type SignalDefinition = components["schemas"]["SignalDefinitionDto"];
export type Person = components["schemas"]["PersonDto"];
export type OrgMe = components["schemas"]["OrgMe"];
export type WorkItem = components["schemas"]["WorkItemDto"];
export type WorkEvent = components["schemas"]["WorkEventDto"];
export type Goal = components["schemas"]["GoalDto"];
export type OneOnOne = components["schemas"]["OneOnOneDto"];
export type OneOnOneStats = components["schemas"]["OneOnOneStats"];
export type Action = components["schemas"]["ActionDto"];
export type TenantSettings = components["schemas"]["TenantSettings"];
export type Summary = components["schemas"]["SummaryDto"];
export type AgendaItem = components["schemas"]["AgendaItemDto"];

export const useMe = () => useQuery({ queryKey: ["me"], queryFn: async () => unwrap(await api.GET("/api/v1/me")), staleTime: 5 * 60_000, retry: 3 });
export const useOrgMe = () => useQuery({ queryKey: ["org", "me"], queryFn: async () => unwrap(await api.GET("/api/v1/org/me")) });
export const useManagers = () => useQuery({ queryKey: ["org", "managers"], queryFn: async () => unwrap(await api.GET("/api/v1/org/managers")) });

/** Everyone, not just managers — a vendor account often belongs to an individual
 *  contributor, and the managers list would never offer them. */
export const usePeople = () => useQuery({ queryKey: ["org", "people"], queryFn: async () => unwrap(await api.GET("/api/v1/org/people")) });

export type VendorIdentity = components["schemas"]["IdentityDto"];
export type User = components["schemas"]["UserDto"];

/** Every sign-in in the tenant, with the person each one is linked to. Admin only. */
export const useUsers = () => useQuery({ queryKey: ["org", "users"], queryFn: async () => unwrap(await api.GET("/api/v1/org/users")) });

/** Vendor accounts a connector has seen. Unmapped ones are the queue an admin works. */
export const useIdentities = (unmapped_only = true) =>
  useQuery({
    queryKey: ["org", "identities", unmapped_only],
    queryFn: async () => unwrap(await api.GET("/api/v1/org/identities", { params: { query: { unmapped_only } } })),
  });

export function useMapIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["MapIdentity"]) => unwrap(await api.POST("/api/v1/org/identities/map", { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org"] }),
  });
}
export const usePerson = (key: string) => useQuery({ queryKey: ["org", "person", key], queryFn: async () => unwrap(await api.GET("/api/v1/org/people/{person_key}", { params: { path: { person_key: key } } })) });
export const useReports = (key: string) => useQuery({ queryKey: ["org", "reports", key], queryFn: async () => unwrap(await api.GET("/api/v1/org/people/{person_key}/reports", { params: { path: { person_key: key } } })) });

export const useConnections = (enabled = true) => useQuery({ queryKey: ["connections"], queryFn: async () => unwrap(await api.GET("/api/v1/connections")), refetchInterval: 30_000, enabled });
export const useSyncRuns = (id: string) => useQuery({ queryKey: ["connections", id, "runs"], queryFn: async () => unwrap(await api.GET("/api/v1/connections/{id}/sync-runs", { params: { path: { id }, query: { limit: 20 } } })) });
export function useCreateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateConnection"]) => unwrap(await api.POST("/api/v1/connections", { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}
export function useRevokeConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => unwrap(await api.DELETE("/api/v1/connections/{id}", { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}
export const authorizeUrl = async (id: string) => unwrap(await api.GET("/api/v1/connections/{id}/authorize", { params: { path: { id } } })).authorize_url;
export function useSetCredentials() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & components["schemas"]["ManualCredentials"]) => unwrap(await api.PUT("/api/v1/connections/{id}/credentials", { params: { path: { id } }, body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["connections"] }),
  });
}

export const useSignalDefinitions = () => useQuery({ queryKey: ["signals", "definitions"], queryFn: async () => unwrap(await api.GET("/api/v1/signals/definitions")), staleTime: 10 * 60_000 });
export const useSignals = (q: { signal_key?: string; scope_type?: string; scope_key?: string; from?: string; to?: string; limit?: number }) =>
  useQuery({ queryKey: ["signals", q], queryFn: async () => unwrap(await api.GET("/api/v1/signals", { params: { query: { limit: 500, ...q } } })) });

export const useWork = (q: { scope?: string; kind?: string; connector?: string; open_only?: boolean; stale_days?: number; limit?: number } = {}) =>
  useQuery({ queryKey: ["work", q], queryFn: async () => unwrap(await api.GET("/api/v1/work", { params: { query: q } })) });
export const useWorkEvents = (itemKey: string) => useQuery({ queryKey: ["work", itemKey, "events"], queryFn: async () => unwrap(await api.GET("/api/v1/work/{item_key}/events", { params: { path: { item_key: itemKey } } })) });

export const useGoals = (scope?: string) => useQuery({ queryKey: ["goals", scope], queryFn: async () => unwrap(await api.GET("/api/v1/goals", { params: { query: scope ? { scope } : {} } })) });
export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (body: components["schemas"]["CreateGoal"]) => unwrap(await api.POST("/api/v1/goals", { body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["goals"] }) });
}
export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async ({ id, ...body }: { id: string } & components["schemas"]["GoalProgress"]) => unwrap(await api.PATCH("/api/v1/goals/{id}", { params: { path: { id } }, body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["goals"] }) });
}

export const useOneOnOnes = () => useQuery({ queryKey: ["one-on-ones"], queryFn: async () => unwrap(await api.GET("/api/v1/one-on-ones")) });
export const useOneOnOneStats = () => useQuery({ queryKey: ["one-on-ones", "stats"], queryFn: async () => unwrap(await api.GET("/api/v1/one-on-ones/stats")) });
export function useCreateOneOnOne() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (body: components["schemas"]["CreateOneOnOne"]) => unwrap(await api.POST("/api/v1/one-on-ones", { body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["one-on-ones"] }) });
}
export function useUpdateOneOnOne() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async ({ id, ...body }: { id: string } & components["schemas"]["UpdateOneOnOne"]) => unwrap(await api.PATCH("/api/v1/one-on-ones/{id}", { params: { path: { id } }, body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["one-on-ones"] }) });
}

export const useActions = (q: { scope?: string; state?: string } = {}) => useQuery({ queryKey: ["actions", q], queryFn: async () => unwrap(await api.GET("/api/v1/actions", { params: { query: { limit: 200, ...q } } })) });
export function useCreateAction() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (body: components["schemas"]["CreateAction"]) => unwrap(await api.POST("/api/v1/actions", { body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["actions"] }) });
}
export function useTransitionAction() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async ({ id, ...body }: { id: string } & components["schemas"]["Transition"]) => unwrap(await api.POST("/api/v1/actions/{id}/transition", { params: { path: { id } }, body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["actions"] }) });
}

export const useSettings = () => useQuery({ queryKey: ["settings"], queryFn: async () => unwrap(await api.GET("/api/v1/settings")) });
export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: async (body: components["schemas"]["UpdateSettings"]) => unwrap(await api.PATCH("/api/v1/settings", { body })), onSuccess: () => void qc.invalidateQueries({ queryKey: ["settings"] }) });
}

export const useSummary = (scope_type: string, scope_key: string | undefined) =>
  useQuery({
    queryKey: ["summaries", scope_type, scope_key],
    enabled: !!scope_key,
    retry: false,
    queryFn: async () => {
      const r = await api.GET("/api/v1/summaries", { params: { query: { scope_type, scope_key: scope_key ?? "" } } });
      if (r.response.status === 404) return null;
      return unwrap(r);
    },
  });
export function useGenerateAgenda() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => unwrap(await api.POST("/api/v1/one-on-ones/{id}/agenda", { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["one-on-ones"] }),
  });
}

export type ImportReport = components["schemas"]["ImportReport"];
export function useImportCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (text: string) =>
      unwrap(
        await api.POST("/api/v1/org/import", {
          body: text,
          bodySerializer: (b) => b as string,
          headers: { "content-type": "text/csv" },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org"] }),
  });
}

export type Answer = components["schemas"]["AnswerDto"];
export function useAsk() {
  return useMutation({ mutationFn: async (body: components["schemas"]["AskRequest"]) => unwrap(await api.POST("/api/v1/ask", { body })) });
}
export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["InviteUser"]) => unwrap(await api.POST("/api/v1/org/users/invite", { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org"] }),
  });
}
export function useLinkUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["LinkUser"]) => unwrap(await api.POST("/api/v1/org/users/link", { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org"] }),
  });
}

// ---------------------------------------------------------------- platform admin (MVP-G3)
// Cross-tenant reads. The API only answers these for the platform_admin group on a
// humindly.io login; everyone else gets 403, which ErrorBox shows as-is.
export const useAdminTenants = () =>
  useQuery({ queryKey: ["admin", "tenants"], queryFn: async () => unwrap(await api.GET("/api/v1/admin/tenants")), refetchInterval: 60_000 });
export const useAdminCollection = (id: string, limit = 200) =>
  useQuery({
    queryKey: ["admin", "tenants", id, "collection", limit],
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/tenants/{id}/collection", { params: { path: { id }, query: { limit } } })),
    refetchInterval: 30_000,
  });
export type TenantOverview = components["schemas"]["TenantOverviewDto"];
export type TenantCollection = components["schemas"]["TenantCollectionDto"];
export const useAdminSignals = (id: string, limit = 300) =>
  useQuery({
    queryKey: ["admin", "tenants", id, "signals", limit],
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/tenants/{id}/signals", { params: { path: { id }, query: { limit } } })),
    refetchInterval: 60_000,
  });
export const useRunLineage = (id: string, runId: string | null) =>
  useQuery({
    queryKey: ["admin", "tenants", id, "runs", runId],
    enabled: runId != null,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/tenants/{id}/runs/{run_id}", { params: { path: { id, run_id: runId! } } })),
  });
export type StoredSignal = components["schemas"]["StoredSignalDto"];
export type SignalKey = Pick<StoredSignal, "signal_key" | "scope_type" | "scope_key" | "period_start" | "period_end">;
export const useSignalTrace = (id: string, sig: SignalKey | null) =>
  useQuery({
    queryKey: ["admin", "tenants", id, "trace", sig],
    enabled: sig != null,
    queryFn: async () => unwrap(await api.GET("/api/v1/admin/tenants/{id}/signals/trace", { params: { path: { id }, query: sig! } })),
  });
